<template>
    <div class="CheckboxInput" :class="{ 'CheckboxInput--disabled': disabled }" v-bind="plainAttrs">
        <label class="CheckboxInput__label">
            <input
                class="CheckboxInput__field"
                type="checkbox"
                :name="name"
                :value="value"
                :checked="value === modelValue"
                :disabled="disabled"
                v-on="listeners"
            >
            <span class="CheckboxInput__text">{{ label }}</span>
        </label>
    </div>
</template>

<style lang="scss">
@import "../style-variables.scss";

.CheckboxInput {
    width: 100%;

    &__label {
        @include selection-label;
    }

    &--disabled &__label {
        cursor: not-allowed;
    }

    &__field {
        @include selection-field-base;
        border-radius: 4px;

        &:checked {
            border-color: var(--eidTrustGreen);
            background-color: var(--eidTrustGreen);

            &::after {
                content: "✓";
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: var(--eidWhite);
                font-size: 0.875rem;
                font-weight: bold;
                line-height: 1;
            }
        }
    }

    &__text {
        @include selection-text;

        .CheckboxInput__field:disabled + & {
            color: var(--eidTechGray-50);
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { omitListenerAttrs, toEventListeners } from '@evidentid/vue-commons/utils/attrsListeners';

    export default defineComponent({
        name: 'CheckboxInput',
        inheritAttrs: false,
        props: {
            label: {
                type: String as PropType<string>,
                default: '',
            },
            name: {
                type: String as PropType<string>,
                required: true,
            },
            // The value this checkbox represents when checked (e.g., true, "option1")
            value: {
                type: [ String, Boolean ] as PropType<string | boolean>,
                default: '',
            },
            // The current selected value from parent - checkbox is checked when this matches 'value'
            modelValue: {
                type: [ String, Boolean ] as PropType<string | boolean>,
                default: '',
            },
            disabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        computed: {
            // The handlers are bound to the inner element by `v-on="listeners"`. With
            // inheritAttrs left on, Vue 3 would bind them to this component's root as
            // well, and a native event bubbling out of the inner element would call the
            // caller twice -- which silently undoes a toggle. Ordinary attributes still
            // reach the root through `plainAttrs`.
            plainAttrs(): Record<string, unknown> {
                return omitListenerAttrs(this.$attrs);
            },
            // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): Vue 3 removed the separate
            // `$listeners` object -- parent-supplied event handlers now arrive merged
            // into `$attrs` as `onEventName`. Rebuild the bare-event-name shape
            // `v-on="listeners"` expects.
            listeners(): Record<string, (...args: unknown[]) => void> {
                return toEventListeners(this.$attrs);
            },
        },
    });
</script>
